import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import MiniFlip from "@/components/MiniFlip";
import { CategoryBadge } from "@/lib/categories";
import { getAnonId, getVote, setVote } from "@/lib/voteStore";
import { guestWallReached, recordGuestVote, guestNudge, openAuth, openAuthWall } from "@/lib/guestGate";
import { haptic, VoteBurst, FloatingPlusOne } from "@/components/VoteFX";
import { MessageCircle, TrendingUp, Check, Flame, Eye, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function DebateCard({ topic, index = 0, onVoted }) {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [picked, setPicked] = useState(() => getVote(topic.topic_id));
  const [voting, setVoting] = useState(false);
  const [voteAnim, setVoteAnim] = useState(0);
  const total = topic.votes_a + topic.votes_b;
  const pctA = total > 0 ? Math.round((topic.votes_a / total) * 100) : 50;
  const pctYour = picked === "a" ? pctA : 100 - pctA;
  const goToDebate = () => navigate(`/issue/${topic.topic_id}`);
  const img = topic.theme_image?.url;

  const vote = async (e, side) => {
    e.stopPropagation();
    if (picked || voting) return;
    if (!user && guestWallReached(topic.topic_id)) { openAuthWall(); return; }
    setVoting(true);
    haptic(22);
    try {
      const { data } = await api.post("/votes", { topic_id: topic.topic_id, side, anon_id: getAnonId() });
      setPicked(side);
      setVote(topic.topic_id, side);
      setVoteAnim((v) => v + 1);
      onVoted?.(topic.topic_id, data.votes_a, data.votes_b);
      if (user) {
        if (data.streak?.current_streak > 1) {
          toast.success(`${data.streak.current_streak}-day voting streak!`, { icon: <Flame className="w-4 h-4 text-orange-500" /> });
        }
      } else {
        const n = guestNudge(recordGuestVote(topic.topic_id));
        toast(n.msg, n.action ? { action: { label: "Sign up", onClick: openAuth } } : undefined);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    } finally {
      setVoting(false);
    }
  };

  const btn = (side) => {
    const isA = side === "a";
    const active = picked === side;
    const dim = picked && !active;
    return (
      <motion.button
        onClick={(e) => vote(e, side)}
        disabled={!!picked || voting}
        whileTap={{ scale: 0.96 }}
        animate={dim ? { opacity: 0.4, scale: 0.97 } : { opacity: 1, scale: 1 }}
        className={`relative flex-1 min-w-0 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-bold rounded-xl border-2 transition-colors duration-300 ${
          active
            ? isA ? "bg-view-a border-view-a text-white shadow-md" : "bg-view-b border-view-b text-white shadow-md"
            : isA ? "border-view-a/40 text-view-a bg-view-a-light hover:bg-view-a hover:text-white hover:border-view-a"
                  : "border-view-b/40 text-view-b bg-view-b-light hover:bg-view-b hover:text-white hover:border-view-b"
        }`}
        data-testid={`card-vote-${side}-${topic.topic_id}`}
      >
        {active && <Check className="w-4 h-4 shrink-0" />}
        <span className="truncate">{isA ? topic.side_a_label : topic.side_b_label}</span>
      </motion.button>
    );
  };

  return (
    <motion.div
      role="link"
      tabIndex={0}
      onClick={goToDebate}
      onKeyDown={(e) => e.key === "Enter" && goToDebate()}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: Math.min(index, 6) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
      className={`relative cursor-pointer bg-white rounded-2xl shadow-card hover:shadow-card-hover transition-shadow duration-300 overflow-hidden group ${
        picked ? (picked === "a" ? "ring-2 ring-view-a" : "ring-2 ring-view-b") : ""
      }`}
      data-testid={`debate-card-${topic.topic_id}`}
    >
      {img && (
        <div className="relative h-36 sm:h-44 overflow-hidden bg-gradient-to-br from-view-a/30 to-view-b/30">
          <img src={img} alt="" loading="lazy" referrerPolicy="no-referrer"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
          <div className="absolute top-3 left-3"><CategoryBadge category={topic.category} /></div>
          {topic.theme_image?.credit && (
            <span className="absolute bottom-1.5 right-2 text-[9px] text-white/60">Photo: {topic.theme_image.credit}</span>
          )}
        </div>
      )}

      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          {img ? <span /> : <CategoryBadge category={topic.category} />}
          <div className="flex items-center gap-3 text-neutral">
            <AnimatePresence mode="popLayout">
              <motion.span key={total} initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 1.3, opacity: 0 }}
                className="flex items-center gap-1 text-xs tabular-nums" data-testid={`card-total-${topic.topic_id}`}>
                <TrendingUp className="w-3.5 h-3.5" /> {total}
              </motion.span>
            </AnimatePresence>
            <span className="flex items-center gap-1 text-xs"><MessageCircle className="w-3.5 h-3.5" /> {topic.comment_count || 0}</span>
          </div>
        </div>

        <h3 className="font-heading text-xl sm:text-2xl tracking-tight text-dark leading-tight mb-4 group-hover:text-view-a transition-colors">
          {topic.question}
        </h3>

        <div className="relative flex items-center gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
          {btn("a")}
          <span className="text-[10px] text-neutral font-bold shrink-0">VS</span>
          {btn("b")}
          <VoteBurst trigger={voteAnim} color={picked === "b" ? "#FFC300" : "#7B2CBF"} />
          <FloatingPlusOne trigger={voteAnim} color={picked === "b" ? "#FFC300" : "#7B2CBF"} />
        </div>

        {!picked ? (
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral font-medium" data-testid={`reveal-hint-${topic.topic_id}`}>
            <Eye className="w-3.5 h-3.5" /> Tap a side to reveal what everyone thinks
          </div>
        ) : (
          <MiniFlip votesA={topic.votes_a} votesB={topic.votes_b} kick />
        )}

        <AnimatePresence>
          {picked && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden" data-testid={`vote-reveal-${topic.topic_id}`}>
              <button onClick={(e) => { e.stopPropagation(); goToDebate(); }}
                className="mt-3 w-full flex items-center justify-between gap-2 text-left group/cta">
                <span className={`text-sm font-bold ${picked === "a" ? "text-view-a" : "text-view-b"}`}>
                  {pctYour < 50
                    ? `You're in the ${pctYour}% minority 👀`
                    : `You're with the ${pctYour}% majority`}
                </span>
                <span className="shrink-0 flex items-center gap-1 text-xs font-semibold text-neutral group-hover/cta:text-view-a transition-colors">
                  {pctYour < 50 ? "Defend your side" : "Join the debate"} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
